import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { useDisplays, convertToDisplayWithOwner } from '@/hooks/useDisplays';
import DisplayCategories from '@/components/displays/DisplayCategories';
import { DisplayWithOwner } from '@/data/mockDisplaysData';

// Export DisplayWithOwner type to resolve import issues
export type { DisplayWithOwner } from '@/data/mockDisplaysData';
import { AdvancedSearchService, SearchFilters } from '@/lib/advancedSearch';
import { LocationService } from '@/lib/locationService';
import DisplayFiltersSidebar from '@/components/displays/DisplayFiltersSidebar';
import { FiltersState, defaultFiltersState } from '@/lib/filter-options';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { GeospatialSearch } from '@/lib/search/utils/GeospatialSearch';
import { TypesenseSearchService } from '@/lib/search/typesense/SearchService';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

// categories removed; filters sidebar provides UI

const Displays = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [filters, setFilters] = useState<FiltersState>({ ...defaultFiltersState, query: initialQuery });
  const [advancedResults, setAdvancedResults] = useState<any[]>([]);
  const [isAdvancedSearchLoading, setIsAdvancedSearchLoading] = useState(false);
  const [useAdvancedSearch, setUseAdvancedSearch] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationDenied, setLocationDenied] = useState<boolean | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalResults, setTotalResults] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isServerSearchEnabled, setIsServerSearchEnabled] = useState(true);
  const [filteredDisplays, setFilteredDisplays] = useState<DisplayWithOwner[]>([]);

  const { data: displays = [], isLoading: isLoadingFromHook, error } = useDisplays();

  // Check if we should use advanced search based on URL parameters
  useEffect(() => {
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const holiday = searchParams.get('holiday');
    const type = searchParams.get('type');
    const zip = searchParams.get('zip');

    // Use advanced search if we have geographic or specific filter parameters
    const shouldUseAdvanced = !!(city || state || holiday || type || zip);
    setUseAdvancedSearch(shouldUseAdvanced);

    if (shouldUseAdvanced) {
      performAdvancedSearch();
    }
  }, [searchParams]);

  // Sync filters.query when URL q changes
  useEffect(() => {
    setFilters((prev) => ({ ...prev, query: initialQuery }));
  }, [initialQuery]);

  // Parse filters from URL on mount or when URL changes
  useEffect(() => {
    const urlTypes = (searchParams.get('types') || '').split(',').filter(Boolean);
    const urlExp = (searchParams.get('exp') || '').split(',').filter(Boolean);
    const urlHol = (searchParams.get('hol') || '').split(',').filter(Boolean);
    const minRating = parseFloat(searchParams.get('minRating') || '0');
    const radiusMiles = parseInt(searchParams.get('radius') || `${defaultFiltersState.radiusMiles}`, 10);
    const sortBy = (searchParams.get('sortBy') as any) || defaultFiltersState.sortBy;
    setFilters((prev) => ({
      ...prev,
      types: urlTypes as any,
      experiences: urlExp as any,
      holidays: urlHol as any,
      minRating: Number.isNaN(minRating) ? prev.minRating : minRating,
      radiusMiles: Number.isNaN(radiusMiles) ? prev.radiusMiles : radiusMiles,
      sortBy,
    }));
  }, [searchParams]);

  // Perform advanced search with geographic filtering
  const performAdvancedSearch = async () => {
    setIsAdvancedSearchLoading(true);
    try {
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const holiday = searchParams.get('holiday');
      const type = searchParams.get('type');
      const zip = searchParams.get('zip');
      const query = searchParams.get('q');

      // Build search filters
      const filters: SearchFilters = {
        limit: 100,
        sortBy: 'relevance'
      };

      // Add text query if present
      if (query) {
        filters.query = query;
      }

      // Add location-based filtering
      if (city || state || zip) {
        let locationText = '';
        if (city && state) {
          locationText = `${city}, ${state}`;
        } else if (city) {
          locationText = city;
        } else if (state) {
          locationText = state;
        } else if (zip) {
          locationText = zip;
        }
        
        if (locationText) {
          filters.locationText = locationText;
          
          // Try to geocode for radius-based search
          try {
            const geocoded = await LocationService.geocodeAddress(locationText);
            if (geocoded) {
              filters.location = {
                latitude: geocoded.latitude,
                longitude: geocoded.longitude
              };
              filters.radius = 50000; // 50km radius
            }
          } catch (error) {
            console.warn('Failed to geocode location:', locationText);
          }
        }
      }

      // Add holiday filtering
      if (holiday) {
        const holidayMap: Record<string, string> = {
          'christmas': 'Christmas',
          'halloween': 'Halloween',
          'musical': 'LightShow', // This maps to display_type
          'drive-thru': 'Drive-Through'
        };
        
        if (holiday === 'musical') {
          filters.displayTypes = ['LightShow'];
        } else if (holidayMap[holiday]) {
          filters.holidayTypes = [holidayMap[holiday]];
        }
      }

      // Add type filtering  
      if (type) {
        const typeMap: Record<string, string> = {
          'musical': 'LightShow',
          'drive-thru': 'Drive-Through',
          'synchronized': 'LightShow',
          'family': 'Residential'
        };
        
          if (typeMap[type]) {
            if (!filters.displayTypes) filters.displayTypes = [];
            filters.displayTypes.push(typeMap[type]);
            if (!filters.holidayTypes) filters.holidayTypes = [];
          }
      }

      console.log('Advanced search filters:', filters);
      
      // Perform the search
      const searchResponse = await AdvancedSearchService.search(filters);
      
      // Transform results to match our display format
      const transformedResults = searchResponse.results.map((result: any) => ({
        id: result.id,
        name: result.title,
        location: result.location || '',
        description: result.description || '',
        image_url: result.imageUrl || '',
        latitude: result.latitude || 0,
        longitude: result.longitude || 0,
        display_type: result.displayType || '',
        holidays: result.holidayType ? [result.holidayType] : [],
        rating: result.rating || 0,
        year_started: result.yearStarted || null,
        tags: [],
        // Add required fields that might be missing
        schedule: '',
        owner: null
      }));

      setAdvancedResults(transformedResults);
    } catch (error) {
      console.error('Advanced search failed:', error);
      // Fall back to basic search if advanced search fails
      setUseAdvancedSearch(false);
    } finally {
      setIsAdvancedSearchLoading(false);
    }
  };

  // Map UI sort to Typesense sort string (best-effort)
  const mapSortByToTypesense = (sortBy?: string) => {
    switch (sortBy) {
      case 'upvotes':
        return 'upvote_count:desc,_text_match:desc';
      case 'trending':
        return 'hot_score:desc,_text_match:desc';
      case 'rating':
        return 'average_rating:desc';
      case 'recentlyAdded':
        return 'created_at:desc';
      case 'recentlyUpdated':
        return 'updated_at:desc';
      case 'songCount':
        return 'songs_count:desc';
      case 'alphaAsc':
        return 'display_name:asc';
      case 'alphaDesc':
        return 'display_name:desc';
      default:
        return undefined;
    }
  };

  // Server-side search with Typesense
  useEffect(() => {
    const performServerSearch = async () => {
      if (!isServerSearchEnabled) return;
      
      setIsLoading(true);
      try {
        const mappedSortBy = mapSortByToTypesense(filters.sortBy);
        const searchParams = {
          query: filters.query || '',
          filters: {
            // Map UI filters to Typesense field names
            display_type: filters.types.length > 0 ? filters.types : undefined,
            experience_type: filters.experiences.length > 0 ? filters.experiences : undefined,
            holiday_type: filters.holidays.length > 0 ? filters.holidays : undefined,
            min_rating: filters.minRating > 0 ? filters.minRating : undefined,
            // Add other filter mappings as needed
          },
          location: currentLocation ? { lat: currentLocation.latitude, lng: currentLocation.longitude } : undefined,
          radius_km: filters.radiusMiles > 0 ? filters.radiusMiles * 1.60934 : undefined, // Convert miles to km
          // Only pass sort_by if we can map it to a valid Typesense sort string.
          // Leaving it undefined lets the service use its default relevance sort.
          sort_by: mappedSortBy,
          page: currentPage,
          per_page: itemsPerPage,
        };

        const result = await TypesenseSearchService.searchDisplays(searchParams);
        
        if (result.hits) {
          const mappedDisplays = result.hits.map((hit: any) => ({
            id: hit.document.id,
            name: hit.document.display_name,
            description: hit.document.description,
            city: hit.document.city,
            state: hit.document.state,
            country: hit.document.country,
            latitude: hit.document.latitude,
            longitude: hit.document.longitude,
            featured: hit.document.featured,
            rating: hit.document.rating,
            songCount: hit.document.song_count,
            createdAt: hit.document.created_at,
            updatedAt: hit.document.updated_at,
            // Typesense 'displays' index currently lacks image_url. We'll enrich below from Supabase.
            image_url: '',
            // Add required DisplayWithOwner fields
            isFavorite: false,
            location: [hit.document.city, hit.document.state].filter(Boolean).join(', '),
            averageRating: hit.document.rating || 0,
            ratingCount: 0,
            creator: null,
          } as DisplayWithOwner));

          // Hydrate missing images from Supabase for these IDs
          try {
            const ids = mappedDisplays.map((d) => Number(d.id));
            const { data: imgRows } = await supabase
              .from('displays')
              .select('id,image_url')
              .in('id', ids as any);
            const idToImage: Record<string, string> = {};
            (imgRows || []).forEach((row: any) => {
              if (row && row.id != null) {
                idToImage[String(row.id)] = row.image_url || '';
              }
            });
            mappedDisplays.forEach((d) => {
              // Prefer fetched image when available
              (d as any).image_url = idToImage[String(d.id)] || (d as any).image_url || '';
            });
          } catch (e) {
            console.warn('Failed to hydrate display images from Supabase', e);
          }
          
          setFilteredDisplays(mappedDisplays);
          setTotalResults(result.found || 0);
        }
      } catch (error) {
        console.error('Server search failed:', error);
        setFilteredDisplays([]);
        setTotalResults(0);
      } finally {
        setIsLoading(false);
      }
    };

    performServerSearch();
  }, [filters.query, filters.types, filters.experiences, filters.holidays, filters.minRating, filters.radiusMiles, filters.sortBy, currentLocation, currentPage, isServerSearchEnabled]);

  // Use filtered displays from server-side search
  const displaysToShow = isServerSearchEnabled ? filteredDisplays : displays;

  // Pagination logic
  const totalPages = Math.ceil(totalResults / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDisplays = displaysToShow.slice(startIndex, endIndex);

  // Convert to DisplayWithOwner format for rendering
  const displayItems: DisplayWithOwner[] = currentDisplays.map(display => 
    convertToDisplayWithOwner(display) as DisplayWithOwner
  );


  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCurrentLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocationDenied(false);
      },
      () => setLocationDenied(true),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // On filters change, sync URL
  const onFiltersChange = (next: FiltersState) => {
    setFilters(next);
    const p = new URLSearchParams(searchParams);
    if (next.query) p.set('q', next.query); else p.delete('q');
    if (next.types.length) p.set('types', next.types.join(',')); else p.delete('types');
    if (next.experiences.length) p.set('exp', next.experiences.join(',')); else p.delete('exp');
    if (next.holidays.length) p.set('hol', next.holidays.join(',')); else p.delete('hol');
    if (next.minRating) p.set('minRating', String(next.minRating)); else p.delete('minRating');
    if (next.radiusMiles) p.set('radius', String(next.radiusMiles)); else p.delete('radius');
    if (next.sortBy) p.set('sortBy', next.sortBy); else p.delete('sortBy');
    p.set('page', '1');
    setSearchParams(p);
  };

  // Determine loading state
  const isDisplaysLoading = useAdvancedSearch ? isAdvancedSearchLoading : isLoading;
  
  // Generate page description based on search parameters
  const getPageDescription = () => {
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const holiday = searchParams.get('holiday');
    
    if (city && state && holiday) {
      return `Find ${holiday} light displays in ${city}, ${state}. Browse spectacular holiday light shows, get directions, and read reviews.`;
    } else if (city && state) {
      return `Discover holiday light displays in ${city}, ${state}. Find the best Christmas and Halloween light shows near you.`;
    } else if (state && holiday) {
      return `Browse ${holiday} light displays throughout ${state}. Find amazing holiday light shows and displays.`;
    }
    
    return "Explore holiday light displays around the world. Find magical experiences near you or plan your next light-hunting adventure.";
  };

  // Component initialization effects
  useEffect(() => {
    // Initialize current page from URL
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    if (!Number.isNaN(pageParam) && pageParam > 0 && pageParam !== currentPage) {
      setCurrentPage(pageParam);
    }
  }, [searchParams]);

  // Reset pagination when filters/search change
  useEffect(() => {
    // Reset to first page when query or filters change and sync URL
    setCurrentPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', '1');
    setSearchParams(newParams);
  }, [filters.query, filters.types, filters.experiences, filters.holidays, filters.minRating, filters.radiusMiles, useAdvancedSearch, isAdvancedSearchLoading]);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="flex-1 pt-24 pb-0">
        <div className="max-w-full h-[calc(100vh-96px)]">

          {/* Desktop layout: left sidebar + right list */}
          <div className="h-full flex">
            <div className="hidden md:block h-full overflow-y-auto">
              <DisplayFiltersSidebar
                value={filters}
                onChange={onFiltersChange}
                onClearAll={() => onFiltersChange({ ...defaultFiltersState })}
                onUseCurrentLocation={useCurrentLocation}
                locationEnabled={locationDenied === false}
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 sm:px-6 py-6">
                <HeaderSection />

                {/* Active filter chips */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {filters.query && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, query: '' })}>
                      {filters.query} ×
                    </Badge>
                  )}
                  {filters.types.map((v) => (
                    <Badge key={v} variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, types: filters.types.filter((t) => t !== v) })}>
                      {v} ×
                    </Badge>
                  ))}
                  {filters.experiences.map((v) => (
                    <Badge key={v} variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, experiences: filters.experiences.filter((t) => t !== v) })}>
                      {v} ×
                    </Badge>
                  ))}
                  {filters.holidays.map((v) => (
                    <Badge key={v} variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, holidays: filters.holidays.filter((t) => t !== v) })}>
                      {v} ×
                    </Badge>
                  ))}
                  {filters.minRating > 0 && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, minRating: 0 })}>
                      Rating ≥ {filters.minRating} ×
                    </Badge>
                  )}
                  {currentLocation && (
                    <Badge variant="secondary" className="cursor-pointer" onClick={() => onFiltersChange({ ...filters, radiusMiles: defaultFiltersState.radiusMiles })}>
                      Within {filters.radiusMiles} mi ×
                    </Badge>
                  )}
                </div>
                <DisplayContentSection />
                <PaginationBar />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <div className="md:hidden">
        <Footer />
      </div>
    </div>
  );

  // Helper component for header section
  function HeaderSection() {
    const getTitle = () => {
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const holiday = searchParams.get('holiday');
      
      if (city && state && holiday) {
        return `${holiday.charAt(0).toUpperCase() + holiday.slice(1)} Displays in ${state}`;
      } else if (city && state) {
        return `Holiday Light Displays in ${city}, ${state}`;
      } else if (state && holiday) {
        return `${holiday.charAt(0).toUpperCase() + holiday.slice(1)} Displays in ${state}`;
      }
      
      return "Discover Light Displays";
    };

    const getResultsText = () => {
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const holiday = searchParams.get('holiday');
      
      const parts = [];
      if (city) parts.push(`in ${city}`);
      if (state && !city) parts.push(`in ${state}`);
      if (holiday) parts.push(`for ${holiday}`);
      
      const locationPart = parts.join(' ');
      const resultCount = useAdvancedSearch ? displaysToShow.length : totalResults;
      
      return `Found ${resultCount} display${resultCount !== 1 ? 's' : ''} ${locationPart}${useAdvancedSearch ? ' using geographic search' : ''}`;
    };

    return (
      <div className="mb-4">
        {/* Main header row with title, results count, and controls */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              {getTitle()}
            </h1>
            <p className="text-sm text-muted-foreground">
              {getResultsText()}
            </p>
          </div>
          
          {/* Desktop filters button */}
          <div className="hidden md:flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-full">
                <DisplayFiltersSidebar
                  value={filters}
                  onChange={onFiltersChange}
                  onClearAll={() => onFiltersChange({ ...defaultFiltersState })}
                  onUseCurrentLocation={useCurrentLocation}
                  locationEnabled={locationDenied === false}
                />
              </SheetContent>
            </Sheet>
            
            {/* Sort controls */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Sort by</label>
              <select
                className="border rounded-md px-2 py-1 text-sm bg-background min-w-[120px]"
                value={filters.sortBy}
                onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as any })}
              >
                <option value="upvotes">Most Upvoted</option>
                <option value="trending">Trending</option>
                <option value="relevance">Relevance</option>
                <option value="distance">Distance</option>
                <option value="popularity">Popularity</option>
                <option value="rating">Rating</option>
                <option value="recentlyAdded">Recently Added</option>
                <option value="recentlyUpdated">Recently Updated</option>
                <option value="openingSoon">Opening Soon</option>
                <option value="songCount">Song Count</option>
                <option value="alphaAsc">Name A-Z</option>
                <option value="alphaDesc">Name Z-A</option>
              </select>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onFiltersChange({ ...defaultFiltersState })}
                className="text-xs"
              >
                Clear all
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile controls row */}
        <div className="md:hidden flex items-center justify-between gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full">
              <DisplayFiltersSidebar
                value={filters}
                onChange={onFiltersChange}
                onClearAll={() => onFiltersChange({ ...defaultFiltersState })}
                onUseCurrentLocation={useCurrentLocation}
                locationEnabled={locationDenied === false}
              />
            </SheetContent>
          </Sheet>
          
          <div className="flex items-center gap-2 flex-1 justify-end">
            <select
              className="border rounded-md px-2 py-1 text-sm bg-background flex-1 max-w-[140px]"
              value={filters.sortBy}
              onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as any })}
            >
              <option value="upvotes">Upvotes</option>
              <option value="trending">Trending</option>
              <option value="relevance">Relevance</option>
              <option value="distance">Distance</option>
              <option value="popularity">Popularity</option>
              <option value="rating">Rating</option>
              <option value="recentlyAdded">Recent</option>
              <option value="recentlyUpdated">Updated</option>
              <option value="alphaAsc">A-Z</option>
              <option value="alphaDesc">Z-A</option>
            </select>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onFiltersChange({ ...defaultFiltersState })}
              className="text-xs px-2"
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Helper component for display content
  function DisplayContentSection() {
    if (isDisplaysLoading) {
      return (
        <div className="text-center py-12">
          <h3 className="text-xl font-medium mb-2">
            {useAdvancedSearch ? 'Searching displays...' : 'Loading displays...'}
          </h3>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12 text-destructive">
          <h3 className="text-xl font-medium mb-2">Error loading displays</h3>
          <p>{(error as Error).message}</p>
        </div>
      );
    }

    return (
      <DisplayCategories 
        displayItems={displayItems}
      />
    );
  }

  // Pagination bar component
  function PaginationBar() {
    if (totalPages <= 1) return null;
    const goTo = (page: number) => {
      const clamped = Math.min(Math.max(1, page), totalPages);
      if (clamped === currentPage) return;
      setCurrentPage(clamped);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(clamped));
      setSearchParams(newParams);
    };
    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, totalResults)} of {totalResults}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded border ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent'}`}
          >
            Previous
          </button>
          <div className="text-sm">
            Page {currentPage} of {totalPages}
          </div>
          <button
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded border ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent'}`}
          >
            Next
          </button>
        </div>
      </div>
    );
  }
};

export default Displays;
